import { localizedPath } from '../i18n/routing'

/**
 * Rendering for build-time content pages: guides, FAQ, legal text.
 *
 * Long-form copy lives in JSON catalogs like every other string, as blocks of
 * text with a deliberately tiny inline syntax — `code`, **strong** and
 * [label](href) — so translators edit prose, not HTML. Everything is escaped
 * before markup is applied, so a catalog cannot inject HTML.
 *
 * Pure string functions: imported by Vite configs, which cannot load Vue.
 */

export type Messages = Record<string, unknown>
export type Translate = (key: string, params?: Record<string, string | number>) => string

/** A paragraph, or one of the few structures prose needs. */
export type Block =
  | string
  | { h3: string }
  | { list: readonly string[] }
  | { steps: readonly string[] }
  | { note: string }
  | { code: string }

export const esc = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

/** Looks a dotted key up in `messages`, then `fallback`; `{name}` placeholders as vue-i18n. */
export function createTranslator(messages: Messages, fallback: Messages): Translate {
  return (key, params = {}) => {
    const value = lookup(messages, key) ?? lookup(fallback, key)
    if (typeof value !== 'string') return key
    return value.replace(/\{(\w+)\}/g, (_, name: string) => String(params[name] ?? ''))
  }
}

/** Raw catalog value — for structured content (blocks, lists) a translator cannot return. */
export function lookup(messages: Messages, key: string): unknown {
  return key
    .split('.')
    .reduce<unknown>((node, part) => (node as Messages | undefined)?.[part], messages)
}

/**
 * Inline markup to HTML. Site-relative links are localized, so a Portuguese
 * reader following one stays in Portuguese; external links open as usual.
 */
export function renderInline(text: string, locale: string): string {
  return esc(text)
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_, label: string, href: string) => {
      if (href.startsWith('/')) return `<a href="${localizedPath(href, locale)}">${label}</a>`
      return `<a href="${href}" rel="noopener">${label}</a>`
    })
}

/** The same text with the markup removed, for meta tags and structured data. */
export function plainText(text: string): string {
  return text
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\[([^\]]+)\]\([^)\s]+\)/g, '$1')
}

export function renderBlocks(blocks: readonly Block[], locale: string): string {
  const inline = (text: string): string => renderInline(text, locale)
  return blocks
    .map((block) => {
      if (typeof block === 'string') return `<p>${inline(block)}</p>`
      if ('h3' in block) return `<h3>${inline(block.h3)}</h3>`
      if ('list' in block)
        return `<ul>${block.list.map((item) => `<li>${inline(item)}</li>`).join('')}</ul>`
      if ('steps' in block)
        return `<ol>${block.steps.map((item) => `<li>${inline(item)}</li>`).join('')}</ol>`
      if ('note' in block) return `<p class="doc__note">${inline(block.note)}</p>`
      return `<pre class="doc__code"><code>${esc(block.code)}</code></pre>`
    })
    .join('\n')
}
